import { API_CONFIG } from '../config/api';
import { secureTokenManager } from '../utils/secureTokenManager';

// Admin User interfaces
export interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  role: string;
  accountStatus: 'enabled' | 'disabled';
  createdOn: string;
  lastLogin: string;
}

export interface AdminUserListResponse {
  success: boolean;
  data: {
    users: AdminUser[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
  message?: string;
}

export interface AdminUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: 'enabled' | 'disabled';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateAdminUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: 'user' | 'moderator';
  status?: 'enabled' | 'disabled';
}

export interface UpdateAdminUserRequest {
  fullName?: string;
  role?: string;
  accountStatus?: 'enabled' | 'disabled';
}

export interface AdminUserResponse {
  success: boolean;
  data: AdminUser;
  message?: string;
}

export interface AdminUserStats {
  totalUsers: number;
  enabledUsers: number;
  disabledUsers: number;
  adminUsers: number;
  managerUsers: number;
  monthToDate: number;
  yearToDate: number;
}

export class AdminUserService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authToken = secureTokenManager.getAdminSessionToken();

    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    // Debug logging removed for security

    try {
      const response = await fetch(url, config);
      
      // Response logging removed for security
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Admin User API Error:', errorData);
        throw new Error(
          errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Success logging removed for security
      return data;
    } catch (error) {
      console.error('💥 Admin User API Request Failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get list of admin users with pagination
  async getAdminUsers(filters: AdminUserFilters = {}): Promise<AdminUserListResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add search parameter
    if (filters.search) params.append('search', filters.search);
    
    // Add role filter
    if (filters.role && filters.role !== 'all') {
      params.append('role', filters.role);
    }
    
    // Add status filter
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    // Add sorting parameters
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const endpoint = `/api/admin/users${queryString ? `?${queryString}` : ''}`;
    
    // Endpoint logging removed for security
    return this.makeRequest<AdminUserListResponse>(endpoint);
  }

  // Get single admin user by ID
  async getAdminUser(id: string): Promise<AdminUser> {
    const endpoint = `/api/admin/users/${id}?_t=${Date.now()}`;
    const response = await this.makeRequest<{ data: AdminUser; success: boolean }>(endpoint);
    return response.data;
  }

  // Create a non-admin console user
  async createAdminUser(userData: CreateAdminUserRequest): Promise<AdminUserResponse> {
    return this.makeRequest<AdminUserResponse>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Update admin user
  async updateAdminUser(id: string, userData: UpdateAdminUserRequest): Promise<AdminUserResponse> {
    const endpoint = `/api/admin/users/${id}`;
    return this.makeRequest<AdminUserResponse>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Delete admin user
  async deleteAdminUser(id: string): Promise<{ success: boolean; message: string }> {
    const endpoint = `/api/admin/users/${id}`;
    return this.makeRequest<{ success: boolean; message: string }>(endpoint, {
      method: 'DELETE',
    });
  }

  // Update user status
  async updateUserStatus(id: string, status: 'enabled' | 'disabled'): Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      fullName: string;
      email: string;
      role: string;
      previousStatus: string;
      newStatus: string;
      updatedAt: string;
    };
  }> {
    const endpoint = `/api/admin/users/${id}/status`;
    return this.makeRequest<{
      success: boolean;
      message: string;
      data: {
        id: string;
        fullName: string;
        email: string;
        role: string;
        previousStatus: string;
        newStatus: string;
        updatedAt: string;
      };
    }>(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Change user password
  async changeUserPassword(id: string, currentPassword: string, newPassword: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const endpoint = `/api/admin/users/${id}/password`;
    return this.makeRequest<{
      success: boolean;
      message: string;
    }>(endpoint, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Get admin user statistics
  async getAdminUserStats(): Promise<AdminUserStats> {
    const endpoint = `/api/admin/users/stats?_t=${Date.now()}`;
    const response = await this.makeRequest<{
      data: AdminUserStats;
      success: boolean;
    }>(endpoint);
    
    return response.data;
  }

  // Test API connection
  async testConnection(): Promise<boolean> {
    try {
      // Connection test logging removed for security
      await this.makeRequest<AdminUserListResponse>('/api/admin/users?page=1&limit=1');
      return true;
    } catch (error) {
      console.error('❌ Admin User API connection failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const adminUserService = new AdminUserService();
